/**
 * Fixtures List View - Match list with filtering
 */
import { useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import MatchCard from './MatchCard';
import RecordResultModal from './RecordResultModal';
import QuickResultModal from './QuickResultModal';

export default function FixturesListView({ fixtures, isAdmin, eventId, onRefresh }) {
  const [filter, setFilter] = useState('all'); // 'all', 'scheduled', 'completed', 'live'
  const [roundFilter, setRoundFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showQuickResultModal, setShowQuickResultModal] = useState(false);
  const [showScorerModal, setShowScorerModal] = useState(null);
  const [scorerEmail, setScorerEmail] = useState('');

  // Get unique round types and groups for filters
  const roundTypes = [...new Set(fixtures.map(f => f.round_type))];
  const groups = [...new Set(fixtures.filter(f => f.group_name).map(f => f.group_name))];

  // Apply filters
  const filteredFixtures = fixtures.filter(f => {
    if (filter !== 'all' && f.match_status !== filter) return false;
    if (roundFilter !== 'all' && f.round_type !== roundFilter) return false;
    if (groupFilter !== 'all' && f.group_name !== groupFilter) return false;
    return true;
  });

  const handleRecordResult = (fixture) => {
    setSelectedFixture(fixture);
    setShowResultModal(true);
  };

  const handleQuickResult = (fixture) => {
    setSelectedFixture(fixture);
    setShowQuickResultModal(true);
  };

  const handleQuickResultSubmit = async (payload) => {
    try {
      const res = await axiosInstance.post(
        `/v1/events/${eventId}/live/${selectedFixture.id}/set-final-result`,
        payload,
      );
      const summary = res.data?.result_summary;
      toast.success(summary ? `Quick Result · ${summary}` : 'Quick Result applied');
      setShowQuickResultModal(false);
      setSelectedFixture(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply Quick Result');
    }
  };

  const handleResultSubmit = async (result) => {
    try {
      await axiosInstance.patch(`/v1/events/${eventId}/fixtures/${selectedFixture.id}/result`, result);
      toast.success('Result recorded successfully');
      setShowResultModal(false);
      setSelectedFixture(null);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record result');
    }
  };

  const handleRevertResult = async (fixtureId) => {
    if (!window.confirm('Revert this match result? This will update standings.')) return;

    try {
      await axiosInstance.patch(`/v1/events/${eventId}/fixtures/${fixtureId}/revert`);
      toast.success('Result reverted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revert result');
    }
  };

  const handleReopenMatch = async (fixtureId) => {
    if (!window.confirm(
      'Reopen this live-scored match for further scoring?\n\n'
      + '• Awards (Player of the Match, etc.) will be cleared\n'
      + '• Ball-by-ball data and innings are PRESERVED\n'
      + '• Standings will be recalculated once you finalise again\n\n'
      + 'Continue?'
    )) return;

    try {
      await axiosInstance.post(`/v1/events/${eventId}/fixtures/${fixtureId}/reopen`);
      toast.success('Match reopened · ball-by-ball data preserved');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen match');
    }
  };

  const handleRefinaliseMatch = async (fixtureId) => {
    if (!window.confirm(
      'Re-finalise this match?\n\n'
      + 'Recomputes winner, result summary, and standings from preserved ball-by-ball data. '
      + 'Safe to use for any match whose innings are complete but status is stuck.'
    )) return;

    try {
      const res = await axiosInstance.post(`/v1/events/${eventId}/fixtures/${fixtureId}/refinalise`);
      const summary = res.data?.result_summary;
      if (res.data?.pending_tie) {
        toast.warning('Match is tied — accept tie or trigger super over from the scorer console.');
      } else {
        toast.success(summary ? `Match finalised · ${summary}` : 'Match finalised');
      }
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to re-finalise match');
    }
  };

  const handleDeleteFixture = async (fixtureId) => {
    if (!window.confirm('Delete this fixture?')) return;

    try {
      await axiosInstance.delete(`/v1/events/${eventId}/fixtures/${fixtureId}`);
      toast.success('Fixture deleted');
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete fixture');
    }
  };

  const handleAssignScorer = async () => {
    if (!showScorerModal || !scorerEmail.trim()) return;
    try {
      await axiosInstance.post(`/v1/events/${eventId}/live/${showScorerModal.id}/scorers`, {
        email: scorerEmail.trim(),
      });
      toast.success('Scorer assigned');
      setScorerEmail('');
      setShowScorerModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign scorer');
    }
  };

  const formatRoundType = (type) => {
    const labels = {
      'league': 'League',
      'group': 'Group Stage',
      'quarter_final': 'Quarter Final',
      'semi_final': 'Semi Final',
      'third_place': '3rd Place',
      'final': 'Final',
      'round_of_16': 'Round of 16',
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4" data-testid="fixtures-list-view">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {/* Status Filter */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {['all', 'scheduled', 'live', 'completed'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filter === status
                  ? 'bg-white text-[#2B3A67] shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>

        {/* Round Filter */}
        {roundTypes.length > 1 && (
          <select
            value={roundFilter}
            onChange={e => setRoundFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-[#3AAFA9] focus:outline-none"
          >
            <option value="all">All Rounds</option>
            {roundTypes.map(type => (
              <option key={type} value={type}>{formatRoundType(type)}</option>
            ))}
          </select>
        )}

        {/* Group Filter */}
        {groups.length > 0 && (
          <select
            value={groupFilter}
            onChange={e => setGroupFilter(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-3 py-2 focus:border-[#3AAFA9] focus:outline-none"
          >
            <option value="all">All Groups</option>
            {groups.map(group => (
              <option key={group} value={group}>Group {group}</option>
            ))}
          </select>
        )}

        <span className="text-xs text-gray-400 ml-auto">
          {filteredFixtures.length} match{filteredFixtures.length !== 1 ? 'es' : ''}
        </span>
      </div>

      {/* Fixtures List */}
      {filteredFixtures.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <i className="ph-bold ph-calendar-x text-3xl mb-2"></i>
          <p className="text-sm">No matches found for selected filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredFixtures.map(fixture => (
            <MatchCard
              key={fixture.id}
              fixture={fixture}
              isAdmin={isAdmin}
              eventId={eventId}
              onRecordResult={() => handleRecordResult(fixture)}
              onQuickResult={() => handleQuickResult(fixture)}
              onRevertResult={() => handleRevertResult(fixture.id)}
              onReopenMatch={() => handleReopenMatch(fixture.id)}
              onRefinaliseMatch={() => handleRefinaliseMatch(fixture.id)}
              onDelete={() => handleDeleteFixture(fixture.id)}
              onAssignScorer={(f) => setShowScorerModal(f)}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}

      {/* Record Result Modal */}
      {showResultModal && selectedFixture && (
        <RecordResultModal
          fixture={selectedFixture}
          onSubmit={handleResultSubmit}
          onClose={() => {
            setShowResultModal(false);
            setSelectedFixture(null);
          }}
        />
      )}

      {/* Quick Result Modal */}
      {showQuickResultModal && selectedFixture && (
        <QuickResultModal
          fixture={selectedFixture}
          onSubmit={handleQuickResultSubmit}
          onClose={() => {
            setShowQuickResultModal(false);
            setSelectedFixture(null);
          }}
        />
      )}

      {/* Assign Scorer Modal */}
      {showScorerModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
          onClick={() => setShowScorerModal(null)}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-sm overflow-hidden max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="assign-scorer-modal"
          >
            <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-bold text-lg text-[#2B3A67]">Assign Scorer</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Match #{showScorerModal.match_number}: {showScorerModal.team_a_name || 'TBD'} vs {showScorerModal.team_b_name || 'TBD'}
                </p>
              </div>
              <button
                onClick={() => setShowScorerModal(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 flex-shrink-0"
                aria-label="Close"
                data-testid="assign-scorer-close"
              >×</button>
            </div>
            <div className="p-5 overflow-y-auto">
              <label className="text-sm font-medium text-gray-700 block mb-2">Scorer Email</label>
              <input
                type="email"
                value={scorerEmail}
                onChange={e => setScorerEmail(e.target.value)}
                placeholder="Enter scorer's email address"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:border-[#3AAFA9] focus:outline-none"
                data-testid="scorer-email-input"
              />
              <p className="text-xs text-gray-400 mt-2">
                The scorer must have an existing account. Admins and Super Admins have automatic scorer access.
              </p>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3">
              <button
                onClick={() => { setShowScorerModal(null); setScorerEmail(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAssignScorer}
                disabled={!scorerEmail.trim()}
                className="flex-1 py-2.5 bg-[#2B3A67] text-white rounded-xl font-bold hover:bg-[#1e2a4a] disabled:opacity-50"
                data-testid="assign-scorer-submit"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
